// Préférences terrain : retour haptique, écran allumé, unité, main dominante.
//
// Elles vivent en base pour suivre le joueur d'un appareil à l'autre, mais sont
// miroitées dans localStorage. La raison est concrète : la bascule du tiroir
// doit réagir instantanément, même sur le réseau d'un practice, et l'écriture
// serveur peut arriver après. L'état local fait foi pour l'affichage, le
// serveur pour la persistance.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from './api.ts';
import { useAuth } from './auth.tsx';
import type { DistanceUnit } from './units.ts';

export interface UserSettings {
    hapticsEnabled: boolean;
    hapticsIntensity: number;
    wakeLockEnabled: boolean;
    /** Choix explicite du joueur, ou null s'il suit le serveur. */
    distanceUnit: DistanceUnit | null;
    /** Ce que propose le back-office. */
    serverDistanceUnit: DistanceUnit;
    /** Ce qui s'applique réellement — la seule valeur à utiliser pour afficher. */
    effectiveDistanceUnit: DistanceUnit;
    distanceStepM: number;
    dominantHand: 'right' | 'left';
    /** `null` = non déclaré, et les deux barèmes fédéraux s'affichent alors. */
    gender: 'femme' | 'homme' | null;
    /** Le plafond du handicap de jeu, en coups reçus sur dix-huit trous.
     *
     *  La formule fédérale n'en connait pas ; l'usage en applique un, et les
     *  applications officielles l'affichent — 36 le plus souvent. Il ne concerne
     *  que les index qui le dépassent, donc les débutants. `null` le retire. */
    maxHandicapStrokes: number | null;
}

const DEFAULTS: UserSettings = {
    hapticsEnabled: true,
    hapticsIntensity: 2,
    wakeLockEnabled: true,
    distanceUnit: null,
    serverDistanceUnit: 'm',
    effectiveDistanceUnit: 'm',
    distanceStepM: 5,
    dominantHand: 'right',
    gender: null,
    maxHandicapStrokes: 36,
};

const STORAGE_KEY = 'golf.settings';

function readLocal(): UserSettings {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<UserSettings>) } : DEFAULTS;
    } catch {
        return DEFAULTS;
    }
}

interface SettingsState {
    settings: UserSettings;
    /** Optimiste : l'affichage change tout de suite, l'écriture suit. */
    update: (patch: Partial<UserSettings>) => void;
    /** Attend la confirmation du serveur — pour un écran de réglages, où l'on
     *  veut savoir si l'enregistrement a réussi. */
    save: (patch: Partial<UserSettings>) => Promise<void>;
    unit: DistanceUnit;
}

const SettingsContext = createContext<SettingsState | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [settings, setSettings] = useState<UserSettings>(readLocal);

    const apply = useCallback((s: UserSettings) => {
        setSettings(s);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    }, []);

    useEffect(() => {
        if (!user) return;
        void api<UserSettings>('/me/settings')
            .then(apply)
            .catch(() => { /* on garde le miroir local */ });
    }, [user, apply]);

    const update = useCallback((patch: Partial<UserSettings>) => {
        setSettings((prev) => {
            const next = { ...prev, ...patch };
            // L'unité effective se recalcule localement pour que l'affichage
            // suive sans attendre l'aller-retour serveur.
            next.effectiveDistanceUnit = next.distanceUnit ?? next.serverDistanceUnit;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
        void api<UserSettings>('/me/settings', { method: 'PATCH', body: patch })
            .then(apply)
            .catch(() => { /* un échec réseau n'annule pas le geste */ });
    }, [apply]);

    const save = useCallback(async (patch: Partial<UserSettings>) => {
        const fresh = await api<UserSettings>('/me/settings', { method: 'PATCH', body: patch });
        apply(fresh);
    }, [apply]);

    const value = useMemo<SettingsState>(
        () => ({ settings, update, save, unit: settings.effectiveDistanceUnit }),
        [settings, update, save],
    );
    return <SettingsContext value={value}>{children}</SettingsContext>;
}

export function useSettings(): SettingsState {
    const ctx = useContext(SettingsContext);
    if (!ctx) throw new Error('useSettings doit être utilisé dans un SettingsProvider');
    return ctx;
}
